// Tour State Management with @preact/signals
// Manages active tours, progress, and completion tracking via localStorage
// Supports three-state tracking: unseen | dismissed | completed

import { signal, computed, effect } from '@preact/signals'
import { resetIntroTurnRun } from './ionIntroTurn.js'
import { bumpIonIntroGen } from './ionIntroGen.js'
import { safeLocal } from '../utils/safeStorage.js'

/** Fire-and-forget analytics event via dynamic import (avoids circular dep) */
function _track(eventType, tourId, stepIndex = null) {
  import('./tourSyncService.js')
    .then(m => m.trackTourEvent(eventType, tourId, stepIndex))
    .catch(() => {})
}

// ---------------------------------------------------------------------------
// Active tour signals (unchanged)
// ---------------------------------------------------------------------------

export const activeTour = signal(null) // 'benchmarking' | 'explorer-grid' | 'graph' | etc.
export const currentStep = signal(0) // Current step index
export const tourPaused = signal(false) // User can pause/resume
export const showTourPrompt = signal(false) // Show "Start Tour" prompt nudge
export const showWhatsNext = signal(false) // Show "What's next?" panel after Welcome Tour

// ---------------------------------------------------------------------------
// New primary signal: per-tour rich state
// { [tourId]: { status: 'unseen'|'dismissed'|'completed', lastStep: number|null, startedAt: ISO|null, completedAt: ISO|null } }
// ---------------------------------------------------------------------------

export const tourStates = signal({})

// TourPrompt: which tour the current prompt is suggesting
export const activeTourPromptId = signal(null)

// ---------------------------------------------------------------------------
// localStorage init + migration
// ---------------------------------------------------------------------------

// safeLocal, not bare localStorage: touching the property throws SecurityError when
// storage is blocked, and this runs at module scope — an unguarded read would take
// every tour (and whatever imports this module) down with it.
if (typeof window !== 'undefined') {
  const storedNew = safeLocal.get('tours_state')
  const storedOld = safeLocal.get('tours_completed')

  if (storedNew) {
    // New format exists — load it directly
    try {
      tourStates.value = JSON.parse(storedNew)
    } catch (e) {
      console.warn('[Tour] Failed to parse tours_state:', e)
      tourStates.value = {}
    }
  } else if (storedOld) {
    // Migrate old boolean map to new rich state format
    try {
      const oldData = JSON.parse(storedOld)
      const migrated = {}
      for (const [id, completed] of Object.entries(oldData)) {
        if (completed === true) {
          migrated[id] = { status: 'completed', lastStep: null, startedAt: null, completedAt: null }
        }
      }
      tourStates.value = migrated
      console.log('[Tour] Migrated tours_completed to tours_state:', migrated)
    } catch (e) {
      console.warn('[Tour] Failed to migrate tours_completed:', e)
      tourStates.value = {}
    }
    // Remove old key after migration
    safeLocal.remove('tours_completed')
  }
}

// Persist tourStates to localStorage whenever it changes
effect(() => {
  if (typeof window !== 'undefined') {
    safeLocal.set('tours_state', JSON.stringify(tourStates.value))
  }
})

// ---------------------------------------------------------------------------
// Backward-compatible computed: { [tourId]: boolean }
// Existing consumers (TourWhatsNext, hasSeenTour) read from this unchanged
// ---------------------------------------------------------------------------

export const tourCompleted = computed(() => {
  const result = {}
  for (const [id, state] of Object.entries(tourStates.value)) {
    result[id] = state.status === 'completed'
  }
  return result
})

// Computed: Check if current tour is completed
export const currentTourCompleted = computed(() => {
  if (!activeTour.value) return false
  return tourCompleted.value[activeTour.value] === true
})

// ---------------------------------------------------------------------------
// Query functions
// ---------------------------------------------------------------------------

/**
 * Check if user has completed a specific tour (backward-compatible)
 * @param {string} tourId - Tour identifier
 * @returns {boolean}
 */
export function hasSeenTour(tourId) {
  return tourCompleted.value[tourId] === true
}

/**
 * Get the full status of a specific tour
 * @param {string} tourId - Tour identifier
 * @returns {'unseen'|'dismissed'|'completed'}
 */
export function getTourStatus(tourId) {
  const state = tourStates.value[tourId]
  if (!state) return 'unseen'
  return state.status
}

// ---------------------------------------------------------------------------
// Action functions: write to tourStates
// ---------------------------------------------------------------------------

/**
 * Mark a tour as started — sets minimum 'dismissed' status with startedAt timestamp.
 * Starting a tour means the user has at least seen it, so it's dismissed if not completed.
 * @param {string} tourId - Tour identifier
 */
export function markTourStarted(tourId) {
  const existing = tourStates.value[tourId]
  // If already completed, don't downgrade status
  if (existing && existing.status === 'completed') return
  tourStates.value = {
    ...tourStates.value,
    [tourId]: {
      status: 'dismissed',
      lastStep: 0,
      startedAt: existing?.startedAt ?? new Date().toISOString(),
      completedAt: null,
    },
  }
  _track('tour_started', tourId)
}

/**
 * Mark a tour as dismissed (user skipped or closed without completing)
 * Preserves existing lastStep and startedAt, clears completedAt
 * @param {string} tourId - Tour identifier
 */
export function markTourDismissed(tourId) {
  const existing = tourStates.value[tourId]
  tourStates.value = {
    ...tourStates.value,
    [tourId]: {
      status: 'dismissed',
      lastStep: existing?.lastStep ?? null,
      startedAt: existing?.startedAt ?? null,
      completedAt: null,
    },
  }
  _track('tour_abandoned', tourId, existing?.lastStep ?? null)
  console.log(`[Tour] Marked "${tourId}" as dismissed`)
}

/**
 * Mark a tour as completed (persists to localStorage)
 * @param {string} tourId - Tour identifier
 */
export function markTourComplete(tourId) {
  const existing = tourStates.value[tourId]
  tourStates.value = {
    ...tourStates.value,
    [tourId]: {
      status: 'completed',
      lastStep: null,
      startedAt: existing?.startedAt ?? null,
      completedAt: new Date().toISOString(),
    },
  }
  _track('tour_completed', tourId)
  console.log(`[Tour] Marked "${tourId}" as completed`)
}

// ---------------------------------------------------------------------------
// Navigation / lifecycle functions
// ---------------------------------------------------------------------------

/**
 * Start a tour from the beginning
 * @param {string} tourId - Tour identifier
 */
export function startTour(tourId) {
  activeTour.value = tourId
  currentStep.value = 0
  tourPaused.value = false
  showTourPrompt.value = false
  markTourStarted(tourId)
  // ion-intro fires ONE real, paid Ion turn per run (see ionIntroTurn.js).
  // startTour() is the only thing that means "a new run" — Back/Next only
  // move `currentStep` — so the per-run guard is re-armed here, synchronously.
  // Imported statically: ionIntroTurn.js does not depend on this module, so
  // there is no cycle (tourDefinitions.js does, which is why the reset does
  // not live there).
  if (tourId === 'ion-intro') resetIntroTurnRun()
  console.log(`[Tour] Started tour: ${tourId}`)
}

/**
 * Move to next step (or complete if last step)
 * @param {number} totalSteps
 */
export function nextStep(totalSteps) {
  if (currentStep.value < totalSteps - 1) {
    const nextStepIndex = currentStep.value + 1
    // Update lastStep in tourStates
    if (activeTour.value) {
      const existing = tourStates.value[activeTour.value]
      if (existing) {
        tourStates.value = {
          ...tourStates.value,
          [activeTour.value]: { ...existing, lastStep: nextStepIndex },
        }
      }
    }
    currentStep.value = nextStepIndex
    _track('tour_step_reached', activeTour.value, nextStepIndex)
  } else {
    // Last step reached, complete tour
    completeTour()
  }
}

/**
 * Move to previous step
 */
export function prevStep() {
  if (currentStep.value > 0) {
    const prevStepIndex = currentStep.value - 1
    // Update lastStep in tourStates
    if (activeTour.value) {
      const existing = tourStates.value[activeTour.value]
      if (existing) {
        tourStates.value = {
          ...tourStates.value,
          [activeTour.value]: { ...existing, lastStep: prevStepIndex },
        }
      }
    }
    currentStep.value = prevStepIndex
  }
}

/**
 * Jump to specific step
 * @param {number} stepIndex
 */
export function goToStep(stepIndex) {
  currentStep.value = stepIndex
}

/**
 * Complete current tour and mark as seen
 */
export function completeTour() {
  if (activeTour.value) {
    const tourId = activeTour.value
    markTourComplete(tourId)
    console.log(`[Tour] Completed tour: ${tourId}`)

    // Show "What's next?" panel after Welcome Tour only
    if (tourId === 'welcome') {
      showWhatsNext.value = true
    }

    // Dismiss "New" badge after completing the Technology Radar tour
    if (tourId === 'technology-radar') {
      import('../state/radarState.js').then(m => m.dismissRadarNewBadge())
    }

    // ion-intro's demo mutates real Explorer filters/sort (source:'ion') —
    // restore the pre-demo baseline so the user never lands in that state.
    if (tourId === 'ion-intro') {
      import('./tourDefinitions.js').then(m => m.restoreIonIntroBaseline())
    }

    // Chain: Benchmarking → Compare tour
    if (tourId === 'benchmarking') {
      endTour()
      // Small delay so the UI settles after benchmarking tour ends
      setTimeout(() => startTour('compare'), 600)
      return
    }
  }
  endTour()
}

/**
 * End current tour without marking as complete
 */
export function endTour() {
  // C3: bump ion-intro's abort generation counter BEFORE clearing
  // activeTour, so intro-drive's beforeShow (still mid-flight, awaiting one
  // of its 3 scripted delays) sees the bump on its next check and bails
  // instead of firing its remaining applyFilterCommand/applyViewCommand
  // calls after the tour has already been skipped/ended.
  // Static import from the dependency-free ionIntroGen.js — the old
  // `import('./tourDefinitions.js').then(m => m.bumpIonIntroGen?.())` could
  // resolve while tourDefinitions.js was still evaluating (it imports this
  // module — a cycle) and hit the counter's temporal dead zone as an
  // unhandled ReferenceError (CI pipeline 2191). The bump is synchronous now.
  if (activeTour.value === 'ion-intro') {
    bumpIonIntroGen()
  }
  activeTour.value = null
  currentStep.value = 0
  tourPaused.value = false
}

/**
 * Skip current tour — marks as dismissed (NOT completed)
 * Key behavioral change: skip != completed
 */
export function skipTour() {
  if (activeTour.value) {
    const tourId = activeTour.value
    markTourDismissed(tourId)
    console.log(`[Tour] Skipped tour: ${tourId}`)

    // Same baseline-revert as completeTour() — skipping ion-intro must not
    // leave the demo's filter/sort mutations in place either.
    if (tourId === 'ion-intro') {
      import('./tourDefinitions.js').then(m => m.restoreIonIntroBaseline())
    }
  }
  endTour()
}

/**
 * Reset tour state (for "Watch Again" functionality)
 * @param {string} tourId - Tour identifier
 */
export function resetTour(tourId) {
  const newStates = { ...tourStates.value }
  delete newStates[tourId]
  tourStates.value = newStates
  console.log(`[Tour] Reset tour: ${tourId}`)
}

/**
 * Reset all tours (for testing/debugging)
 * Clears both tour states and visit counters from localStorage
 */
export function resetAllTours() {
  tourStates.value = {}
  if (typeof window !== 'undefined') {
    safeLocal.remove('tours_state')
    safeLocal.remove('tours_visit_count')
  }
  console.log('[Tour] Reset all tours')
}

// ---------------------------------------------------------------------------
// TourPrompt visit tracking (DISC-04 prep)
// ---------------------------------------------------------------------------

const VISIT_TRIGGER_COUNT = 3

/**
 * Record a visit to an area. Triggers showTourPrompt on the 3rd visit
 * for tours that are still 'unseen'. Guards against firing during active tours.
 * @param {string} areaId - Area identifier (maps to a tour id)
 */
export function recordAreaVisit(areaId) {
  // Don't show prompt while a tour is active
  if (activeTour.value) return

  // Only trigger for unseen tours
  if (getTourStatus(areaId) !== 'unseen') return

  if (typeof window === 'undefined') return

  let visitCounts = {}
  try {
    const stored = safeLocal.get('tours_visit_count')
    if (stored) visitCounts = JSON.parse(stored)
  } catch (e) {
    console.warn('[Tour] Failed to parse tours_visit_count:', e)
  }

  const current = visitCounts[areaId] ?? 0
  const updated = current + 1
  visitCounts[areaId] = updated
  safeLocal.set('tours_visit_count', JSON.stringify(visitCounts))

  // Trigger prompt on the Nth visit
  if (updated >= VISIT_TRIGGER_COUNT) {
    activeTourPromptId.value = areaId
    showTourPrompt.value = true
    console.log(`[Tour] Triggered prompt for "${areaId}" after ${updated} visits`)
  }
}

// ---------------------------------------------------------------------------
// Debug window object
// ---------------------------------------------------------------------------

if (typeof window !== 'undefined') {
  window.__BATEMO_TOURS__ = {
    state: {
      activeTour,
      currentStep,
      tourCompleted,
      tourStates,
      activeTourPromptId,
      showTourPrompt,
      showWhatsNext,
    },
    actions: {
      startTour,
      endTour,
      skipTour,
      completeTour,
      nextStep,
      prevStep,
      goToStep,
      resetTour,
      resetAllTours,
      hasSeenTour,
      getTourStatus,
      markTourDismissed,
      recordAreaVisit,
      dismissWhatsNext: () => {
        showWhatsNext.value = false
      },
    },
  }
}
